import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { Box, Button, Modal, Stack, Text } from '@mantine/core'
import { FiX } from 'react-icons/fi'
import { format, isValid } from 'date-fns'
import { Link } from 'react-router-dom'
import { useStore } from '../../stores/store'
import agent from '../../api/agent'
import LegalConsentCheckbox, { type LegalConsentTextKey } from './LegalConsentCheckbox'
import { toast } from 'react-toastify'

const BANNER_MAX_WIDTH_PX = 500

export type PolicyUpdateBannerLayout = 'app' | 'landing'

type PolicyUpdateBannerProps = { layout?: PolicyUpdateBannerLayout }

function isLegalConsentKey (key: string): key is LegalConsentTextKey {
  return (
    key === 'terms-of-service' ||
    key === 'privacy-policy' ||
    key === 'community-guidelines' ||
    key === 'slop-feed-consent'
  )
}

function PolicyAcceptRow ({
  policyKey,
  checked,
  onChange
}: {
  policyKey: LegalConsentTextKey
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <Box
      sx={{
        padding: 12,
        borderRadius: 10,
        border: '1px solid var(--gs-border)',
        background: 'var(--gs-surface)'
      }}
    >
      <LegalConsentCheckbox
        legalTextKey={policyKey}
        checked={checked}
        onChange={onChange}
      />
    </Box>
  )
}

export default observer(function PolicyUpdateBanner ({
  layout = 'app'
}: PolicyUpdateBannerProps) {
  const { commonStore, userStore } = useStore()
  const { pendingLegalAcceptances } = commonStore
  const [dismissedThisVisit, setDismissedThisVisit] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [checkedByKey, setCheckedByKey] = useState<Record<string, boolean>>({})

  const pending = useMemo(
    () => pendingLegalAcceptances.filter(p => isLegalConsentKey(p.key)),
    [pendingLegalAcceptances]
  )

  const visible = useMemo(() => {
    if (!userStore.isLoggedIn) return false
    if (dismissedThisVisit) return false
    if (commonStore.loadingPendingLegalAcceptances) return false
    return pending.length > 0
  }, [
    userStore.isLoggedIn,
    dismissedThisVisit,
    commonStore.loadingPendingLegalAcceptances,
    pending.length
  ])

  const latestUpdated = useMemo(() => {
    const dates = pending
      .map(p => new Date(p.lastUpdatedAt))
      .filter(d => isValid(d))
    if (dates.length === 0) return null
    return new Date(Math.max(...dates.map(d => d.getTime())))
  }, [pending])

  const allChecked = pending.length > 0 && pending.every(p => checkedByKey[p.key])

  const openModal = () => {
    const initial: Record<string, boolean> = {}
    for (const p of pending) initial[p.key] = false
    setCheckedByKey(initial)
    setModalOpen(true)
  }

  const handleAcceptAll = async () => {
    if (!allChecked) return
    setSubmitting(true)
    try {
      for (const p of pending) {
        await agent.LegalText.accept(p.key, p.currentVersion)
      }
      await commonStore.loadPendingLegalAcceptances()
      setModalOpen(false)
      toast.success('Thank you — your policy acceptances are up to date.')
    } catch {
      toast.error('Could not save acceptances. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!visible) return null

  const dateLabel =
    latestUpdated && isValid(latestUpdated) ? format(latestUpdated, 'MMM d, yyyy') : '—'

  const policyList = pending.map(p => (
    <Text key={p.key} component="span" size="sm" sx={{ color: 'var(--gs-text)' }}>
      <Text component={Link} to={p.policyPath} size="sm" sx={{ color: 'var(--gs-blue)' }}>
        {p.policyName}
      </Text>
      {p !== pending[pending.length - 1] ? ', ' : ''}
    </Text>
  ))

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: layout === 'landing' ? 8 : 4,
          paddingBottom: 4
        }}
      >
        <Box
          role="region"
          aria-label="Policy update notice"
          sx={{
            width: '100%',
            maxWidth: BANNER_MAX_WIDTH_PX,
            boxSizing: 'border-box',
            padding: '8px 38px 10px 12px',
            borderRadius: 12,
            background:
              'linear-gradient(180deg, var(--gs-policy-banner-bg-strong) 0%, var(--gs-policy-banner-bg) 100%)',
            backdropFilter: 'saturate(1.08)',
            border: '1px solid var(--gs-policy-banner-border)',
            boxShadow:
              '0 10px 40px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.07)',
            position: 'relative'
          }}
        >
          <Text
            component="div"
            weight={700}
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 9,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--gs-policy-banner-label)',
              marginBottom: 4,
              lineHeight: 1.3
            }}
          >
            Policy update · {dateLabel}
          </Text>
          <Text
            component="p"
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              lineHeight: 1.45,
              color: 'var(--gs-text)',
              margin: '0 0 8px',
              wordBreak: 'break-word'
            }}
          >
            We updated {policyList}. Review and accept the latest version to stay in good standing.
          </Text>
          <Button
            size="xs"
            variant="filled"
            onClick={openModal}
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              background: 'var(--gs-blue)',
              '&:hover': { background: '#79b8ff' }
            }}
          >
            Review &amp; accept
          </Button>

          <button
            type="button"
            aria-label="Hide policy notice until your next visit"
            onClick={() => {
              setDismissedThisVisit(true)
              toast.info("OK! We'll ask again later.")
            }}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 30,
              height: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--gs-policy-banner-dismiss-bg)',
              border: '1px solid var(--gs-policy-banner-border)',
              borderRadius: 8,
              color: 'var(--gs-muted)',
              cursor: 'pointer'
            }}
          >
            <FiX size={16} />
          </button>
        </Box>
      </Box>

      <Modal
        opened={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title="Accept updated policies"
        size="md"
        centered
      >
        <Stack spacing="md">
          <Text size="sm" sx={{ color: 'var(--gs-muted)' }}>
            Check each box after reading the terms. Upload and other actions may be blocked until you accept.
          </Text>
          {pending.map(p => (
            <PolicyAcceptRow
              key={p.key}
              policyKey={p.key}
              checked={!!checkedByKey[p.key]}
              onChange={(next) => setCheckedByKey(prev => ({ ...prev, [p.key]: next }))}
            />
          ))}
          <Button
            fullWidth
            disabled={!allChecked || submitting}
            loading={submitting}
            onClick={() => void handleAcceptAll()}
          >
            Save acceptances
          </Button>
        </Stack>
      </Modal>
    </>
  )
})
